import { observer } from 'mobx-react-lite';
import React, { PropsWithChildren, useEffect } from 'react';
import { ErrorBoundary } from '../monitoring/error-boundary';
import { CloseButtonProps, Flex, Box, classnames } from '@cad-arc';
import { WorkspacePanelProvider } from './workspace-panel-provider';
import { WorkspacePanel } from './workspace-panel';
import { PanelHeader } from './panel-header';
import { CadNextNotificationPriorityEnumType } from '@mark43/cad-api';
import { reaction } from 'mobx';
import { ResizeEnable, Rnd } from 'react-rnd';
import { debounce } from 'lodash';
import useResizeObserver from 'use-resize-observer';
import { usePanelArea } from './panel-area';
import { usePanelFocusTrap } from './hooks/use-focus-trap';
import { useWorkspaceWindowType } from '../common/hooks/use-workspace-window-type';
import { useWorkspaceStore } from './use-workspace-store';
import { logWarn } from '../monitoring/log';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';

const BLUR_SUSPECT_PANEL_TYPES = new Set(['UNIT_HISTORY', 'DATA_EXCHANGE_RESULTS']);
const MIN_LOG_INTERVAL_MS = 300_000; // 5 minutes

function hasFractional(value: number) {
    return value % 1 !== 0;
}

function getViewportScale() {
    return window.visualViewport?.scale ?? 1;
}

function getRelativeBoundingClientRect(element: HTMLElement, parent: HTMLElement) {
    const elementRect = element.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();

    return {
        top: elementRect.top - parentRect.top,
        right: elementRect.right - parentRect.left,
        bottom: elementRect.bottom - parentRect.top,
        left: elementRect.left - parentRect.left,
        width: elementRect.width,
        height: elementRect.height,
    };
}

const PanelContainer: React.FC<
    React.ComponentProps<'div'> & {
        focused: boolean;
        isPanelAlerting?: boolean;
    }
> = ({ children, focused, isPanelAlerting }) => {
    return (
        <Flex
            w="100%"
            h="100%"
            overflow="hidden"
            backgroundColor="surface.foreground"
            border="sm"
            className={classnames({'arc-blink-border': isPanelAlerting})}
            borderColor={focused && !isPanelAlerting ? 'selected.default' : 'border.default'}
            boxShadow="lg"
            outline={focused || isPanelAlerting ? '1px solid' : 'none'}
            outlineColor={focused && !isPanelAlerting ? 'selected.default' : 'transparent'}
            borderRadius="md"
            flexDirection="column"
            position="relative"
        >
            {children}
        </Flex>
    );
};

export const Panel = observer(function Panel({
    body,
    header = <div />,
    panel,
    onClose,
    onKeyDown,
    testId,
    resizeOptions,
    closeButtonProps,
    tabIndex,
    highestNotificationPriority,
    onBroughtToFront,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
}: PropsWithChildren<{
    panel: WorkspacePanel;
    body: React.ReactElement;
    header?: React.ReactElement;
    onClose?: () => void;
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
    testId?: string;
    closeButtonProps?: CloseButtonProps;
    tabIndex?: number;
    highestNotificationPriority?: CadNextNotificationPriorityEnumType;
    onBroughtToFront?: () => void;
    minWidth?: number;
    minHeight?: number;
    maxWidth?: number;
    maxHeight?: number;
    resizeOptions?: ResizeEnable;
}>): JSX.Element {
    const focusPanel = (): void => {
        panel.focus();
    };

    const panelArea = usePanelArea();
    const currentWindowType = useWorkspaceWindowType();
    const workspaceStore = useWorkspaceStore();

    const rndRef = React.useRef<null | Rnd>(null);
    const panelFocusTrapRef = usePanelFocusTrap<HTMLDivElement>();
    const enabledFeatures = useEnabledFeatures();
    const blurSuspectStateRef = React.useRef<{
        lastIsFractional: boolean;
        lastDpr: number;
        lastViewportScale: number;
        lastLogAt: number;
    } | null>(null);

    const maybeLogBlurSuspectState = React.useCallback(
        (
            source: 'sync' | 'drag-stop' | 'resize-stop' | 'window-resize',
            values: {
                x: number;
                y: number;
                width: number;
                height: number;
            }
        ) => {
            if (!enabledFeatures.blurDiagnosticsEnabled || !BLUR_SUSPECT_PANEL_TYPES.has(panel.workspacePanelType)) {
                return;
            }

            const isFractional =
                hasFractional(values.x) ||
                hasFractional(values.y) ||
                hasFractional(values.width) ||
                hasFractional(values.height);
            const dpr = window.devicePixelRatio;
            const viewportScale = getViewportScale();
            const now = Date.now();
            const prev = blurSuspectStateRef.current;
            const firstObservedFractional = !prev && isFractional;
            const becameFractional = !!prev && !prev.lastIsFractional && isFractional;
            const dprChanged = !!prev && prev.lastDpr !== dpr;
            const viewportScaleChanged = !!prev && prev.lastViewportScale !== viewportScale;
            const shouldLog =
                (firstObservedFractional ||
                    becameFractional ||
                    dprChanged ||
                    viewportScaleChanged) &&
                (!prev || now - prev.lastLogAt >= MIN_LOG_INTERVAL_MS);

            if (shouldLog) {
                const context = {
                    panelType: panel.workspacePanelType,
                    panelId: `${panel.initialId}`,
                    source,
                    x: Number(values.x.toFixed(2)),
                    y: Number(values.y.toFixed(2)),
                    width: Number(values.width.toFixed(2)),
                    height: Number(values.height.toFixed(2)),
                    isFractional,
                    dpr: Number(dpr.toFixed(2)),
                    viewportScale: Number(viewportScale.toFixed(2)),
                    isFocused: panel.isFocused,
                    firstObservedFractional,
                    becameFractional,
                    dprChanged,
                    viewportScaleChanged,
                };
                logWarn('Potential panel blur state detected', context);
            }

            blurSuspectStateRef.current = {
                lastIsFractional: isFractional,
                lastDpr: dpr,
                lastViewportScale: viewportScale,
                lastLogAt: shouldLog ? now : prev?.lastLogAt ?? 0,
            };
        },
        [panel, enabledFeatures.blurDiagnosticsEnabled]
    );

    const setPanelCoordinates = React.useMemo(
        () =>
            debounce(
                (x: number, y: number): void => {
                    panel.setCoordinates(x, y);
                },
                100,
                { trailing: true }
            ),
        [panel]
    );

    const setPanelDimensions = React.useMemo(
        () =>
            debounce(
                (width: number, height: number): void => {
                    panel.setDimensions(width, height);
                },
                100,
                { trailing: true }
            ),
        [panel]
    );

    const synchronizePanelToRnd = React.useCallback(() => {
        setPanelDimensions.cancel();
        setPanelCoordinates.cancel();
        const workspacePanelsContainer = rndRef.current?.getParent() as null | HTMLElement;
        const isActiveWorkspaceWindow =
            panel.windowId === workspaceStore.getActiveWorkspaceWindowId() &&
            currentWindowType === workspaceStore.getActiveWorkspaceWindowType();

        if (isActiveWorkspaceWindow && rndRef.current && workspacePanelsContainer) {
            const workspacePanelsContainerRect = workspacePanelsContainer.getBoundingClientRect();
            const maxWidth = workspacePanelsContainerRect.width;
            const maxHeight = workspacePanelsContainerRect.height;

            const panelElement = rndRef.current.resizableElement.current;

            if (panelElement) {
                const panelElementRect = getRelativeBoundingClientRect(
                    panelElement,
                    workspacePanelsContainer
                );

                const currentWidth = panel.width ?? panelElementRect?.width;
                const currentHeight = panel.height ?? panelElementRect?.height;

                if (currentWidth && currentHeight) {
                    const newWidth = Math.min(currentWidth, maxWidth);
                    const newHeight = Math.min(currentHeight, maxHeight);

                    rndRef.current.updateSize({
                        width: newWidth,
                        height: newHeight,
                    });
                    setPanelDimensions(newWidth, newHeight);

                    const maxX = workspacePanelsContainerRect.width - newWidth;
                    const maxY = workspacePanelsContainerRect.height - newHeight;

                    const currentX = panelElementRect?.left;
                    const currentY = panelElementRect?.top;

                    const xToUse = Math.min(maxX, currentX);
                    const yToUse = Math.min(maxY, currentY);

                    rndRef.current.updatePosition({
                        x: xToUse,
                        y: yToUse,
                    });
                    setPanelCoordinates(xToUse, yToUse);
                    maybeLogBlurSuspectState('sync', {
                        x: xToUse,
                        y: yToUse,
                        width: newWidth,
                        height: newHeight,
                    });
                }
            }
        }
    }, [
        maybeLogBlurSuspectState,
        setPanelDimensions,
        setPanelCoordinates,
        panel.width,
        panel.height,
        workspaceStore,
        currentWindowType,
        panel.windowId,
    ]);

    React.useEffect(() => {
        panel.setSynchronizeLayoutListener(() => {
            synchronizePanelToRnd();
        });
        return () => {
            panel.removeSynchronizeLayoutListener();
        };
    }, [panel, synchronizePanelToRnd]);

    React.useEffect(() => {
        return reaction(
            () => panel.isFocused,
            (value) => {
                // reflow the panel on focus change
                synchronizePanelToRnd();
                if (value) {
                    onBroughtToFront?.();
                }
            },
            { fireImmediately: true }
        );
        // onBroughtToFront is not included in the deps list so that the client doesn't need to put their onBroughtToFront
        // within a useCallback to avoid multiple firing this reaction on re-render.
        //
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panel, synchronizePanelToRnd]);

    // reflow the panel on mount in case its out of bounds
    useEffect(() => {
        synchronizePanelToRnd();
    }, [synchronizePanelToRnd]);

    const debouncedSynchronizePanelToRnd = React.useMemo(
        () => debounce(synchronizePanelToRnd, 500, { trailing: true }),
        [synchronizePanelToRnd]
    );

    // reflow the panels if the "workspace panel area" size changes
    useResizeObserver({
        ref: panelArea.current.resizeObserverRef,
        onResize() {
            debouncedSynchronizePanelToRnd();
        },
    });

    useEffect(() => {
        if (!BLUR_SUSPECT_PANEL_TYPES.has(panel.workspacePanelType)) {
            return;
        }

        const onWindowResize = () => {
            maybeLogBlurSuspectState('window-resize', {
                x: panel.xcoordinate,
                y: panel.ycoordinate,
                width: panel.width ?? 0,
                height: panel.height ?? 0,
            });
        };
        window.addEventListener('resize', onWindowResize);
        return () => window.removeEventListener('resize', onWindowResize);
    }, [maybeLogBlurSuspectState, panel]);

    return (
        <Rnd
            bounds="parent"
            default={{
                x: panel.xcoordinate,
                y: panel.ycoordinate,
                width: panel.width,
                height: panel.height,
            }}
            onDragStop={(e, d) => {
                setPanelCoordinates(d.x, d.y);
                maybeLogBlurSuspectState('drag-stop', {
                    x: d.x,
                    y: d.y,
                    width: panel.width ?? 0,
                    height: panel.height ?? 0,
                });
            }}
            onResizeStop={(e, direction, ref) => {
                const rect = ref.getBoundingClientRect();
                setPanelDimensions(rect.width, rect.height);
                maybeLogBlurSuspectState('resize-stop', {
                    x: panel.xcoordinate,
                    y: panel.ycoordinate,
                    width: rect.width,
                    height: rect.height,
                });
            }}
            minWidth={typeof minWidth !== 'undefined' ? minWidth : panel.minimumDimensions[0]}
            minHeight={typeof minHeight !== 'undefined' ? minHeight : panel.minimumDimensions[1]}
            maxWidth={maxWidth}
            maxHeight={maxHeight}
            enableResizing={resizeOptions ?? true}
            dragHandleClassName="drag-handle"
            ref={rndRef}
            style={{
                zIndex: panel.zindex,
            }}
        >
            <Box
                width="100%"
                height="100%"
                data-testid={testId}
                data-workspace-panel-type={panel.workspacePanelType}
                onKeyDown={(e) => {
                    panel.notifyActivity();
                    onKeyDown?.(e);
                }}
                onKeyDownCapture={() => {
                    panel.notifyActivity();
                }}
                onMouseDown={() => {
                    panel.notifyActivity();
                    focusPanel();
                }}
                onMouseDownCapture={() => {
                    panel.notifyActivity();
                }}
                onTouchStartCapture={() => {
                    panel.notifyActivity();
                }}
                position="absolute"
                tabIndex={tabIndex}
                userSelect="text"
                ref={panelFocusTrapRef}
            >
                <WorkspacePanelProvider workspacePanel={panel}>
                    <PanelContainer focused={panel.isFocused} isPanelAlerting={panel.isAlerting}>
                        <ErrorBoundary
                            name="PANEL"
                            extraTags={{
                                workspacePanelType: panel.workspacePanelType,
                            }}
                            actions={[{ children: 'Close Panel', onClick: () => panel.close() }]}
                            messageVerbNoun="opening a panel"
                        >
                            <PanelHeader
                                panel={panel}
                                onClose={onClose}
                                closeButtonProps={closeButtonProps}
                                highestNotificationPriority={highestNotificationPriority}
                            >
                                {header}
                            </PanelHeader>
                            {body}
                        </ErrorBoundary>
                    </PanelContainer>
                </WorkspacePanelProvider>
            </Box>
        </Rnd>
    );
});
